import React, { useState, useEffect, useMemo } from 'react';
import UserLayout from '@/components/Layout/UserLayout';
import { showSpinner, hideSpinner } from '@/components/Layout/LoadingSpinner';
import Alert from '@/components/UI/Alert';
import Card from '@/components/UI/Card';
import { ResponsiveTableCard, ExpandableDataTable, DateTimePicker } from '@/components/UI';
import type { ColumnDef, Row } from '@/components/UI/dataTableConfig';
import type { EntityTypeUnion } from '@einhaus/project314-types';
import type { EntryLogRecord } from '@/types/appRouter';
import { getDatePlusMinus } from '@einhaus/meteoric-helpers';
import { Link, useSearchParams } from 'react-router-dom';
import EntityLink from '@/components/Entity/EntityLink';
import './EntryLog.tailwind.css';
import { trpc } from '@/api/trpc';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useFetchEntities } from '@/hooks/useFetchEntities';
import { usePortfolios } from '@/hooks/usePortfolios';
import { EntryScoreDisplay } from '@/components/scorecards/EntryScoreDisplay';

// Parse scoring data from entry
const parseScoringData = (record: EntryLogRecord) => {
    try {
        // Check if entry exists and has extraData
        if (!record.entry?.extraData || typeof record.entry.extraData !== 'object') {
            return null;
        }

        if (!('scoringData' in record.entry.extraData)) {
            return null;
        }

        const scoringDataStr = record.entry.extraData.scoringData;

        if (typeof scoringDataStr !== 'string') {
            return null;
        }

        const parsed = JSON.parse(scoringDataStr);

        // Validate required fields exist and are correct type
        if (
            typeof parsed.predictedProfit !== 'number' ||
            typeof parsed.baselineReturn !== 'number' ||
            typeof parsed.adjustmentFactor !== 'number' ||
            typeof parsed.confidence !== 'number'
        ) {
            console.warn('Invalid scoring data structure:', parsed);
            return null;
        }

        return {
            predictedProfit: parsed.predictedProfit,
            baselineReturn: parsed.baselineReturn,
            adjustmentFactor: parsed.adjustmentFactor,
            adjustmentContribution:
                typeof parsed.adjustmentContribution === 'number'
                    ? parsed.adjustmentContribution
                    : parsed.predictedProfit - parsed.baselineReturn,
            matchedRules: typeof parsed.matchedRules === 'number' ? parsed.matchedRules : 0,
            confidence: parsed.confidence,
            decision: parsed.decision === 'accept' || parsed.decision === 'reject' ? parsed.decision : 'reject',
            sizingMultiplier: typeof parsed.sizingMultiplier === 'number' ? parsed.sizingMultiplier : undefined,
            entityNotInExperiment: parsed.entityNotInExperiment === true,
            ruleEvaluations: Array.isArray(parsed.ruleEvaluations) ? parsed.ruleEvaluations : []
        };
    } catch (error) {
        console.error('Failed to parse scoring data:', error);
        return null;
    }
};

const EntryLog: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Use the useSearchParams hook to get and update URL parameters
    const [searchParams] = useSearchParams();

    // Extract parameters from the URL
    const entityIdParam = searchParams.get('entityId') ? Number(searchParams.get('entityId')) : null;
    const entityTypeParam = searchParams.get('entityType') as EntityTypeUnion['entityType'] | null;
    const strategyIdParam = searchParams.get('strategyId') ? Number(searchParams.get('strategyId')) : null;
    const portfolioIdParam = searchParams.get('portfolioId') ? Number(searchParams.get('portfolioId')) : null;
    const entryLogIdParam = searchParams.get('entryLogId') ? Number(searchParams.get('entryLogId')) : null;
    const startDateTimeParam = searchParams.get('startDateTime') || null;
    const endDateTimeParam = searchParams.get('endDateTime') || null;

    // Helper function to parse EST datetime string (YYYY-MM-DD HH:MM:SS) to Date object
    const parseESTDateTimeString = (dateTimeString: string | null): Date | null => {
        if (!dateTimeString) return null;

        try {
            // EST datetime string format: YYYY-MM-DD HH:MM:SS
            // Convert to ISO format for Date constructor
            const [datePart, timePart] = dateTimeString.split(' ');
            if (!datePart) return null;

            // If no time part, use start of day or end of day based on context
            const fullTimePart = timePart || '00:00:00';

            // Create a date object - this will be interpreted in local timezone
            const dateObj = new Date(`${datePart}T${fullTimePart}`);

            if (isNaN(dateObj.getTime())) {
                return null;
            }

            return dateObj;
        } catch (error) {
            console.error('Error parsing date string:', error);
            return null;
        }
    };

    // Helper function to format Date object to EST datetime string (YYYY-MM-DD HH:MM:SS)
    const formatToESTDateTimeString = (date: Date | null): string => {
        if (!date) return getDatePlusMinus('-', 1, 'day', 'ymdhms');

        try {
            // Format to YYYY-MM-DD HH:MM:SS
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');

            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        } catch (error) {
            console.error('Error formatting date to EST string:', error);
            return getDatePlusMinus('-', 1, 'day', 'ymdhms');
        }
    };

    // Initialize dateRange with URL parameters if available, otherwise use defaults
    const initialStartDate = parseESTDateTimeString(startDateTimeParam) || new Date(getDatePlusMinus('-', 1, 'day', 'ymdhms'));

    const initialEndDate = parseESTDateTimeString(endDateTimeParam) || new Date(getDatePlusMinus('+', 1, 'day', 'ymdhms'));

    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([initialStartDate, initialEndDate]);

    // Show loading spinner when component mounts
    useEffect(() => {
        showSpinner();

        // Hide spinner when component unmounts
        return () => {
            hideSpinner();
        };
    }, []);

    // State is now managed by tRPC queries

    // We've already defined searchParams above

    // We've replaced the fetchData function with inline functions
    // in both the initial load useEffect and the onDateRangeChange callback

    // Format date range for queries
    const formattedStartDateTime = useMemo(() => {
        return dateRange[0] ? formatToESTDateTimeString(dateRange[0]) : getDatePlusMinus('-', 1, 'day', 'ymdhms');
    }, [dateRange]);

    const formattedEndDateTime = useMemo(() => {
        return dateRange[1] ? formatToESTDateTimeString(dateRange[1]) : getDatePlusMinus('+', 1, 'day', 'ymdhms');
    }, [dateRange]);

    // Fetch entry logs with tRPC
    const entryLogQuery = useAppQuery(
        {
            ...trpc.user.fetchEntryLog.queryOptions({
                entryLogId: entryLogIdParam ?? undefined,
                startDateTime: formattedStartDateTime,
                endDateTime: formattedEndDateTime,
                entityId: entityIdParam,
                entityType: entityTypeParam,
                strategyId: strategyIdParam,
                portfolioId: portfolioIdParam
            }),
            enabled: true
        },
        {
            fallbackErrorMessage: 'Failed to load entry logs',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    );

    // Fetch portfolios with tRPC
    const portfoliosQuery = usePortfolios({
        errorConfig: {
            fallbackErrorMessage: 'Failed to load portfolios',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    });

    const { portfolios } = portfoliosQuery;

    // Fetch strategies with tRPC
    const strategiesQuery = useAppQuery(
        {
            ...trpc.strategy.fetchStrategies.queryOptions({ ids: [] }),
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load strategies',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    );

    // Extract data from queries
    const entryLogs = useMemo(() => entryLogQuery.data?.entryLogRecords || [], [entryLogQuery.data]);
    const strategies = useMemo(() => strategiesQuery.data?.strategies || [], [strategiesQuery.data]);

    // Fetch entities for entry logs
    const entityIds = useMemo(() => {
        // Use a Map to ensure uniqueness based on entity ID and type
        const entitiesMap = new Map<
            string,
            {
                i: number;
                t: 'a' | 'c' | 'i';
            }
        >();

        // Fetch entities for the trades
        entryLogs.forEach((record) => {
            // Create a unique key for each entity
            const entityKey = `${record.entityId}_${record.entityType}`;

            // Only add if not already in the map
            if (!entitiesMap.has(entityKey)) {
                entitiesMap.set(entityKey, {
                    i: record.entityId,
                    t: record.entityType === 'Asset' ? 'a' : record.entityType === 'Cryptocurrency' ? 'c' : 'i'
                });
            }
        });

        // Convert Map values to an array for the API call
        return Array.from(entitiesMap.values());
    }, [entryLogs]);

    // Fetch entities using the batched hook
    const { entities, isLoading: entitiesLoading } = useFetchEntities(entityIds);

    // Manage loading state
    useEffect(() => {
        const isLoading =
            entryLogQuery.isLoading || portfoliosQuery.isLoading || strategiesQuery.isLoading || (entityIds.length > 0 && entitiesLoading);

        setLoading(isLoading);

        if (isLoading) {
            showSpinner();
        } else {
            hideSpinner();
        }

        // Hide spinner when component unmounts
        return () => {
            hideSpinner();
        };
    }, [entryLogQuery.isLoading, portfoliosQuery.isLoading, strategiesQuery.isLoading, entitiesLoading, entityIds.length]);

    // Entry scoring is optional, so the expander column only earns its space when some row has it.
    const hasExpandableRows = useMemo(() => entryLogs.some((record) => parseScoringData(record) !== null), [entryLogs]);

    const buildPortfolioOutcomes = (record: EntryLogRecord): React.ReactNode => {
        if (!record.messagesForPortfolios) return null;

        const sections: React.ReactNode[] = [];

        for (const portfolioKey of Object.keys(record.messagesForPortfolios)) {
            if (portfolioIdParam && portfolioIdParam !== +portfolioKey) continue;

            const portfolio = portfolios.find((p) => portfolioKey && p.id === +portfolioKey);
            if (!portfolio) continue;

            const messages = record.messagesForPortfolios[portfolioKey];
            if (!messages || messages.length === 0) continue;

            const messageNodes = messages.map((message, index) => {
                const jsonStart = message.indexOf('{');
                const jsonEnd = message.lastIndexOf('}') + 1;

                if (jsonStart === -1 || jsonEnd === -1) {
                    return (
                        <div className="break-words" key={`${portfolio.id}-msg-${index}`}>
                            {message}
                        </div>
                    );
                }

                const json = message.substring(jsonStart, jsonEnd);
                const prefix = message.substring(0, jsonStart).trimEnd();

                try {
                    const parsedJson: unknown = JSON.parse(json);
                    const prettyJson = JSON.stringify(parsedJson, null, 2).replace(/\\n/g, ' ');

                    return (
                        <div className="space-y-1" key={`${portfolio.id}-msg-${index}`}>
                            {prefix ? <div className="break-words">{prefix}</div> : null}
                            <pre className="bg-zinc-900/40 border border-zinc-700 rounded p-2 text-xs whitespace-pre-wrap break-words">
                                {prettyJson}
                            </pre>
                        </div>
                    );
                } catch {
                    return (
                        <div className="break-words" key={`${portfolio.id}-msg-${index}`}>
                            {message}
                        </div>
                    );
                }
            });

            sections.push(
                <div className="border-l-2 border-zinc-700 pl-2" key={`portfolio-${portfolio.id}`}>
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-400">{portfolio.name}</div>
                    <div className="space-y-2 text-sm text-zinc-200">{messageNodes}</div>
                </div>
            );
        }

        if (sections.length === 0) return null;

        return <div className="space-y-3">{sections}</div>;
    };

    // Render expandable row content for scoring data
    const renderSubComponent = ({ row }: { row: Row<EntryLogRecord> }) => {
        const scoringData = parseScoringData(row.original);

        if (!scoringData) {
            return null;
        }

        return (
            <div className="p-4 bg-zinc-800 border-t border-zinc-700">
                <h4 className="text-white text-sm font-semibold mb-3">Entry Scoring</h4>
                <EntryScoreDisplay showDetails scoreResult={scoringData} />
            </div>
        );
    };

    const columns: ColumnDef<EntryLogRecord>[] = [
        {
            accessorKey: 'id',
            header: 'ID',
            minSize: 60,
            size: 70,
            maxSize: 90,
            cell: (info) => <span className="text-xs whitespace-nowrap text-zinc-400 tabular-nums">{info.getValue<number>()}</span>
        },
        {
            // Use accessor function to return a sortable/searchable string
            accessorFn: (row) => {
                const entity = entities.find((entity) => entity.id === row.entityId && entity.entityType === row.entityType);
                if (!entity) return '-';

                if (entity.symbol && entity.name) {
                    return `${entity.symbol} ${entity.name}`;
                } else if (entity.symbol) {
                    return entity.symbol;
                } else if (entity.name) {
                    return entity.name;
                }

                return `-`;
            },
            id: 'entity', // Use id instead of accessorKey when using accessorFn
            header: 'Entity',
            minSize: 140,
            size: 190,
            maxSize: 260,
            cell: (info) => {
                const record = info.row.original;
                const entity = entities.find((entity) => entity.id === record.entityId && entity.entityType === record.entityType);

                if (!entity) return '-';

                return <EntityLink className="text-sm" showName showSymbol entity={entity} />;
            }
        },
        {
            accessorFn: (row) => {
                // Build the outcomes text without HTML for searching
                if (!row.messagesForPortfolios) return '';
                let searchableText = '';

                for (const portfolioKey of Object.keys(row.messagesForPortfolios)) {
                    if (portfolioIdParam && portfolioIdParam !== +portfolioKey) continue;

                    const portfolio = portfolios.find((portfolio) => portfolioKey && portfolio.id === +portfolioKey);
                    if (!portfolio) continue;

                    const messages = row.messagesForPortfolios[portfolioKey];
                    if (!messages) continue;

                    searchableText += `${portfolio.name}: `;

                    for (const message of messages) {
                        // Extract text content, removing any JSON formatting
                        const jsonStart = message.indexOf('{');
                        const jsonEnd = message.lastIndexOf('}') + 1;

                        if (jsonStart === -1 || jsonEnd === -1) {
                            searchableText += `${message} `;
                        } else {
                            const beforeJson = message.substring(0, jsonStart);
                            const json = message.substring(jsonStart, jsonEnd);

                            try {
                                const parsedJson = JSON.parse(json);
                                searchableText += `${beforeJson} ${JSON.stringify(parsedJson)} `;
                            } catch {
                                searchableText += `${message} `;
                            }
                        }
                    }
                }

                return searchableText.trim();
            },
            id: 'outcomes',
            header: 'Outcomes',
            minSize: 300,
            size: 560,
            maxSize: 700,
            cell: (info) => {
                const content = buildPortfolioOutcomes(info.row.original);
                return <div className="entry-log-outcomes">{content}</div>;
            }
        },
        {
            accessorFn: (row) => {
                if (!row.strategyId) return '-';
                const strategy = strategies.find((strategy) => strategy.id === row.strategyId);
                return strategy?.name ? `${strategy.name} ${row.strategyId}` : String(row.strategyId);
            },
            id: 'strategy',
            header: 'Strategy',
            minSize: 180,
            size: 260,
            maxSize: 320,
            cell: (info) => {
                const record = info.row.original;

                if (!record.strategyId) return '-';

                const strategy = strategies.find((strategy) => strategy.id === record.strategyId);

                return (
                    <Link className="text-sm text-primary hover:text-blue-400" to={`/user/tradingStrategy/${record.strategyId}`}>
                        {strategy?.name ?? 'Strategy'}
                        <span className="block text-xs text-zinc-400">#{record.strategyId}</span>
                    </Link>
                );
            }
        },
        {
            accessorKey: 'insertDateTime',
            header: 'Datetime',
            minSize: 120,
            size: 140,
            maxSize: 170,
            cell: (info) => <span className="text-xs whitespace-nowrap text-zinc-400 tabular-nums">{info.getValue<string>()}</span>,
            // Custom sorting function for datetime strings
            sortFn: (rowA, rowB, columnId) => {
                const aValue = rowA.getValue(columnId);
                const bValue = rowB.getValue(columnId);

                // Parse datetime strings to Date objects for proper comparison
                const dateA = new Date(aValue as string);
                const dateB = new Date(bValue as string);

                // Handle invalid dates
                if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
                if (isNaN(dateA.getTime())) return 1;
                if (isNaN(dateB.getTime())) return -1;

                // Compare dates
                return dateA.getTime() - dateB.getTime();
            }
        }
    ];

    // Portfolio title if portfolio filter is active
    const renderPortfolioTitle = () => {
        if (!portfolioIdParam || portfolios.length === 0) return null;

        const portfolio = portfolios.find((portfolio) => portfolio.id === portfolioIdParam);
        if (!portfolio) return null;

        return (
            <span>
                {' '}
                -{' '}
                <Link className="text-primary hover:text-blue-400" to={`/user/portfolio/${portfolio.id}/`}>
                    {portfolio.name}
                </Link>{' '}
                ({portfolio.type})
            </span>
        );
    };

    return (
        <UserLayout containerSize="1600px" title="Entry Log - Project 314">
            <div className="flex justify-between mt-2 sm:mt-3 md:mt-4">
                <div>
                    <h3 className="text-white text-lg sm:text-xl font-semibold mb-2 sm:mb-3 md:mb-4">Entry Log{renderPortfolioTitle()}</h3>
                </div>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            {!entryLogIdParam ? (
                <Card responsivePadding>
                    <div className="flex flex-wrap gap-2 mb-2 sm:mb-3 md:mb-4 items-center">
                        <DateTimePicker
                            endDateTime={dateRange[1]?.toISOString() || ''}
                            startDateTime={dateRange[0]?.toISOString() || ''}
                            onDateRangeChange={(start, end) => {
                                // Show global spinner immediately when Apply is clicked
                                showSpinner();
                                setLoading(true);

                                // Update the date range with proper typing
                                setDateRange([start ? new Date(start) : null, end ? new Date(end) : null] as [Date | null, Date | null]);

                                // The queries will automatically refetch when the dateRange changes
                                // because formattedStartDateTime and formattedEndDateTime are derived from dateRange
                                // and used in the query parameters

                                // Hide spinner after a short delay to allow queries to start
                                setTimeout(() => {
                                    hideSpinner();
                                }, 100);
                            }}
                        />
                    </div>
                </Card>
            ) : (
                <Card responsivePadding>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                        <div className="text-white flex items-center">
                            <svg
                                className="mr-2"
                                fill="currentColor"
                                height="16"
                                viewBox="0 0 16 16"
                                width="16"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                            </svg>
                            Viewing specific entry log with ID: <strong className="font-semibold">{entryLogIdParam}</strong>
                        </div>
                        <Link
                            className="ml-auto px-3 py-1 text-sm border border-blue-500 text-blue-500 rounded hover:bg-blue-500 hover:text-white transition-colors"
                            to="/user/entryLog"
                        >
                            View All Entry Logs
                        </Link>
                    </div>
                </Card>
            )}

            <ResponsiveTableCard noCard className="mt-2 sm:mt-3 md:mt-4 entry-log-card">
                {loading ? (
                    <div className="text-center py-8">
                        <div className="inline-block w-12 h-12 border-4 border-zinc-300 border-t-blue-500 rounded-full animate-spin">
                            <span className="sr-only">Loading...</span>
                        </div>
                        <div className="mt-4 text-zinc-300">Loading data...</div>
                    </div>
                ) : entryLogs.length === 0 ? (
                    <Alert variant="info">No entry logs found for the selected criteria.</Alert>
                ) : (
                    <ExpandableDataTable
                        striped
                        columns={columns}
                        data={entryLogs}
                        getRowCanExpand={(row) => parseScoringData(row.original) !== null}
                        pagination={{ enabled: true, pageSize: 10 }}
                        renderSubComponent={hasExpandableRows ? renderSubComponent : undefined}
                    />
                )}
            </ResponsiveTableCard>
        </UserLayout>
    );
};

export default EntryLog;
